import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../components/Logo";
import PasswordInput from "../components/PasswordInput";
import api, { FALLBACK_PLANS, formatCents } from "../services/api";

const VALID_PLANS = ["starter", "professional", "business"];

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryPlan = (searchParams.get("plan") || "").toLowerCase();
  const initialPlan = VALID_PLANS.includes(queryPlan)
    ? queryPlan
    : "professional";

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [planId, setPlanId] = useState(initialPlan);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null); // {type, text}

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setNotice(null);

    if (form.password !== form.confirmPassword) {
      setNotice({ type: "error", text: "Passwords do not match." });
      return;
    }
    if (form.password.length < 8) {
      setNotice({
        type: "error",
        text: "Password must be at least 8 characters long.",
      });
      return;
    }

    setLoading(true);
    const res = await api.register({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
      companyName: form.companyName,
      planId,
    });
    setLoading(false);

    const next = `/checkout?plan=${planId}&email=${encodeURIComponent(form.email)}`;
    if (res.ok) {
      navigate(next);
    } else if (res.offline) {
      // Demo mode: server unreachable, continue the flow with a notice.
      navigate(`${next}&demo=1`);
    } else {
      setNotice({
        type: "error",
        text: res.message || "Registration failed. Please try again.",
      });
    }
  }

  return (
    <section className="section-container flex justify-center py-16">
      <div className="card w-full max-w-2xl p-8">
        <div className="flex justify-center">
          <Logo withTagline={false} />
        </div>
        <h1 className="mt-6 text-center text-2xl font-bold">
          Create your account
        </h1>
        <p className="mt-1 text-center text-sm text-slate-600">
          One payment, no subscription.
        </p>

        <form onSubmit={handleSubmit} className="mt-8">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="reg-first">
                First Name
              </label>
              <input
                id="reg-first"
                type="text"
                required
                className="input-field"
                placeholder="John"
                value={form.firstName}
                onChange={update("firstName")}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="reg-last">
                Last Name
              </label>
              <input
                id="reg-last"
                type="text"
                required
                className="input-field"
                placeholder="Doe"
                value={form.lastName}
                onChange={update("lastName")}
              />
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="reg-company">
              Company Name
            </label>
            <input
              id="reg-company"
              type="text"
              required
              className="input-field"
              placeholder="Acme Inc."
              value={form.companyName}
              onChange={update("companyName")}
            />
          </div>

          <div className="mt-5">
            <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="reg-email">
              Email Address
            </label>
            <input
              id="reg-email"
              type="email"
              required
              className="input-field"
              placeholder="you@company.com"
              value={form.email}
              onChange={update("email")}
            />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="reg-password">
                Password
              </label>
              <PasswordInput
                id="reg-password"
                required
                placeholder="Min. 8 characters"
                value={form.password}
                onChange={update("password")}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-navy" htmlFor="reg-confirm">
                Confirm Password
              </label>
              <PasswordInput
                id="reg-confirm"
                required
                placeholder="Repeat your password"
                value={form.confirmPassword}
                onChange={update("confirmPassword")}
              />
            </div>
          </div>

          {/* Plan selector */}
          <div className="mt-7">
            <p className="mb-2 text-sm font-medium text-navy">Choose your plan</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {FALLBACK_PLANS.map((plan) => (
                <label
                  key={plan.id}
                  className={`relative cursor-pointer rounded-xl border p-4 transition-colors ${
                    planId === plan.id
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-line hover:border-primary/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    value={plan.id}
                    checked={planId === plan.id}
                    onChange={() => setPlanId(plan.id)}
                    className="sr-only"
                  />
                  {plan.popular && (
                    <span className="absolute -top-2.5 left-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-white">
                      Most Popular
                    </span>
                  )}
                  <span className="block text-sm font-semibold text-navy">
                    {plan.name}
                  </span>
                  <span className="mt-1 block text-lg font-bold text-navy">
                    {formatCents(plan.priceCents)}
                    <span className="text-xs font-normal text-slate-500">
                      once
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <label className="mt-6 flex items-start gap-2.5 text-sm text-slate-600">
            <input
              type="checkbox"
              required
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="form-checkbox mt-0.5 rounded border-line text-primary focus:ring-primary/30"
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" className="font-medium text-primary hover:underline">
                Terms & Conditions
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="font-medium text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          {notice && (
            <div
              className={`mt-5 rounded-lg border px-4 py-3 text-sm ${
                notice.type === "error"
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-blue-200 bg-blue-50 text-blue-700"
              }`}
            >
              {notice.text}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary mt-6 w-full disabled:opacity-60"
          >
            {loading ? "Creating your account..." : "Create Account"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </section>
  );
}
